import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, PersonAdd as PersonAddIcon, EventBusy as FinIcon, ReceiptLong as FactureIcon } from '@mui/icons-material';
import EtablissementFacturation from './EtablissementFacturation';
import { AdminService } from '../../services/adminService';

/**
 * Établissements clients (compte établissement, PLAN_IMPL_COMPTE_ETABLISSEMENT CE-1).
 *
 * 🔴 C'est ici — et seulement ici — que naît un établissement et que son premier gestionnaire
 * est invité : un rôle ne s'attribue pas en libre-service. L'offre est posée à la main (une
 * école paie sur bon de commande) ; le statut de paiement informe, il ne coupe rien. La
 * facturation (conditions négociées, factures Stripe) : `EtablissementFacturation` (CE-8).
 */

const OFFRES = [
  { value: 'premium_plus', label: 'Premium+' },
  { value: 'premium', label: 'Premium' },
];
const STATUTS = [
  { value: 'gratuit', label: 'Gratuit (test)' },
  { value: 'a_facturer', label: 'À facturer' },
  { value: 'facture', label: 'Facturé' },
  { value: 'paye', label: 'Payé' },
];
/** Comment l'élève est nommé dans le jeu (migration 201) — selon le contrat SIGNÉ. */
const NOMS_ELEVE = [
  { value: 'pseudonyme', label: 'Pseudonyme fixe (attribué par l’école)' },
  { value: 'pseudonyme_libre', label: 'Pseudonyme modifiable par l’élève' },
  { value: 'prenom', label: 'Prénom (saisi par l’école, fixe)' },
];
const libelle = (liste, v) => liste.find((x) => x.value === v)?.label || v;
const jour = (d) => (d ? String(d).slice(0, 10) : '');
const messageErreur = (e) => e?.response?.data?.message || e?.message || 'Erreur';

const VIDE = {
  nom: '', offre: 'premium_plus', debut: '', fin: '', statutPaiement: 'gratuit', licences: 30, emailGestionnaire: '',
  nomEleve: 'pseudonyme',
};

function FormulaireEtablissement({ ouvert, initial, onFermer, onEnregistre }) {
  const edition = Boolean(initial?.id);
  const [f, setF] = useState(VIDE);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!ouvert) return;
    setErreur(null);
    setF(edition
      ? { ...VIDE, ...initial, debut: jour(initial.debut), fin: jour(initial.fin), licences: initial.licences }
      : VIDE);
  }, [ouvert, initial, edition]);

  const champ = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const enregistrer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      const corps = {
        nom: f.nom, offre: f.offre, debut: f.debut, fin: f.fin,
        statutPaiement: f.statutPaiement, licences: Number(f.licences), nomEleve: f.nomEleve,
      };
      const res = edition
        ? await AdminService.modifierEtablissement(initial.id, corps)
        : await AdminService.creerEtablissement({ ...corps, emailGestionnaire: f.emailGestionnaire });
      onEnregistre(res);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={ouvert} onClose={onFermer} maxWidth="sm" fullWidth>
      <DialogTitle>{edition ? `Modifier — ${initial.nom}` : 'Nouvel établissement'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erreur && <Alert severity="error">{erreur}</Alert>}
          <TextField label="Nom" value={f.nom} onChange={champ('nom')} required />
          <Stack direction="row" spacing={2}>
            <TextField select label="Offre" value={f.offre} onChange={champ('offre')} fullWidth>
              {OFFRES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField select label="Paiement" value={f.statutPaiement} onChange={champ('statutPaiement')} fullWidth>
              {STATUTS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Début" type="date" value={f.debut} onChange={champ('debut')}
              InputLabelProps={{ shrink: true }} fullWidth required />
            <TextField label="Fin" type="date" value={f.fin} onChange={champ('fin')}
              InputLabelProps={{ shrink: true }} fullWidth required />
          </Stack>
          <TextField label="Licences (élèves)" type="number" value={f.licences} onChange={champ('licences')}
            inputProps={{ min: 0 }} helperText="Une licence libérée ne se réattribue qu'après 24 h." />
          <TextField select label="Nom des élèves dans le jeu" value={f.nomEleve || 'pseudonyme'} onChange={champ('nomEleve')}
            helperText="Selon la convention signée. Le cycle, lui, reste toujours celui de la classe.">
            {NOMS_ELEVE.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          {!edition && (
            <TextField label="E-mail du gestionnaire" type="email" value={f.emailGestionnaire}
              onChange={champ('emailGestionnaire')} required
              helperText="Il reçoit une invitation ; il devra se connecter au portail avec cette adresse." />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFermer}>Annuler</Button>
        <Button variant="contained" onClick={enregistrer} disabled={enCours}>
          {enCours ? <CircularProgress size={20} /> : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function InviterGestionnaire({ etablissement, onFermer, onEnvoye }) {
  const [email, setEmail] = useState('');
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  useEffect(() => { setEmail(''); setErreur(null); }, [etablissement]);

  const envoyer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      onEnvoye(await AdminService.inviterGestionnaire(etablissement.id, email));
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={Boolean(etablissement)} onClose={onFermer} maxWidth="xs" fullWidth>
      <DialogTitle>Inviter un gestionnaire</DialogTitle>
      <DialogContent>
        {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
        <TextField label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          fullWidth sx={{ mt: 1 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onFermer}>Annuler</Button>
        <Button variant="contained" onClick={envoyer} disabled={enCours || !email}>Inviter</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function EtablissementsScreen() {
  const [liste, setListe] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [info, setInfo] = useState(null);
  const [formulaire, setFormulaire] = useState(null); // null | {} (création) | établissement
  const [invitation, setInvitation] = useState(null);
  const [facturation, setFacturation] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      setListe(await AdminService.getEtablissements());
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  /**
   * 🔴 Fin de test : les élèves remis à leur famille quittent leur classe, TOUS les autres comptes
   * (élèves non remis, profils de jeu des enseignants) sont SUPPRIMÉS. Confirmation par le nom exact.
   */
  const finDeTest = async (e) => {
    const saisie = window.prompt(
      `Fin de test pour « ${e.nom} ».\n\nLes élèves dont la famille a utilisé son code ont déjà quitté l'établissement. TOUS les comptes restants (élèves, profils de jeu des enseignants) seront supprimés définitivement.\n\nRecopiez le nom exact pour confirmer :`,
    );
    if (saisie === null) return;
    try {
      const b = await AdminService.finDeTestEtablissement(e.id, saisie);
      setInfo({ severity: b.echecs?.length ? 'warning' : 'success', texte: `Fin de test : ${b.retires} élève(s) rendus à leur famille, ${b.supprimes} supprimé(s), ${b.profils} profil(s) enseignant supprimé(s)${b.echecs?.length ? `, ${b.echecs.length} échec(s) — voir les journaux` : ''}.` });
      charger();
    } catch (err) {
      setErreur(messageErreur(err));
    }
  };

  /** Le mail d'invitation peut ne pas partir : le lien est alors rendu, à transmettre à la main. */
  const direInvitation = (inv) => {
    if (!inv) return;
    setInfo(inv.envoye
      ? { severity: 'success', texte: 'Invitation envoyée par e-mail.' }
      : { severity: 'warning', texte: `L'e-mail n'est pas parti. Lien à transmettre : ${inv.lien}` });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Établissements</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormulaire({})}>
          Nouvel établissement
        </Button>
      </Stack>
      {info && <Alert severity={info.severity} onClose={() => setInfo(null)} sx={{ mb: 2, wordBreak: 'break-all' }}>{info.texte}</Alert>}
      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
      {chargement ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Établissement</TableCell>
                <TableCell>Offre</TableCell>
                <TableCell>Période</TableCell>
                <TableCell>Paiement</TableCell>
                <TableCell align="right">Classes</TableCell>
                <TableCell align="right">Licences</TableCell>
                <TableCell>Gestionnaires</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {liste.length === 0 && (
                <TableRow><TableCell colSpan={8}>Aucun établissement.</TableCell></TableRow>
              )}
              {liste.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{e.nom}</TableCell>
                  <TableCell>{libelle(OFFRES, e.offre)}</TableCell>
                  <TableCell>{jour(e.debut)} → {jour(e.fin)}</TableCell>
                  <TableCell>{libelle(STATUTS, e.statutPaiement)}</TableCell>
                  <TableCell align="right">{e.classes}</TableCell>
                  <TableCell align="right">{e.licencesAttribuees} / {e.licences}</TableCell>
                  <TableCell>
                    {(e.gestionnaires || []).length === 0
                      ? <Chip size="small" label="invitation en attente" />
                      : e.gestionnaires.map((g) => <Chip key={g.uid} size="small" label={g.email || g.nom} sx={{ mr: 0.5 }} />)}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<EditIcon />} onClick={() => setFormulaire(e)}>Modifier</Button>
                    <Button size="small" startIcon={<PersonAddIcon />} onClick={() => setInvitation(e)}>Gestionnaire</Button>
                    <Button size="small" startIcon={<FactureIcon />} onClick={() => setFacturation(e)}>Facturation</Button>
                    <Button size="small" color="error" startIcon={<FinIcon />} onClick={() => finDeTest(e)}>Fin de test</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <FormulaireEtablissement
        ouvert={Boolean(formulaire)}
        initial={formulaire}
        onFermer={() => setFormulaire(null)}
        onEnregistre={(res) => { setFormulaire(null); direInvitation(res?.invitation); charger(); }}
      />
      <EtablissementFacturation
        etablissement={facturation}
        onFermer={() => setFacturation(null)}
        onChange={charger}
      />
      <InviterGestionnaire
        etablissement={invitation}
        onFermer={() => setInvitation(null)}
        onEnvoye={(inv) => { setInvitation(null); direInvitation(inv); charger(); }}
      />
    </Box>
  );
}
